import React from "react";
import { Box } from "@mui/material";
import type { TurnBudgetState } from "../hooks/useStreamingTurn";

export interface BudgetMeterProps {
  budget: TurnBudgetState | undefined;
}

/** Live per-turn budget bar: fraction of maxTurnCostUsd consumed so far.
 *  Turns amber (via the `budget-meter-bar--warn` class) once the reducer has
 *  flagged budget.warn (soft cap crossed, still under the hard cap). */
export function BudgetMeter({ budget }: BudgetMeterProps) {
  if (!budget) return null;
  const { cumulativeCostUsd = 0, maxTurnCostUsd } = budget;
  const fraction = maxTurnCostUsd > 0 ? Math.min(1, Math.max(0, cumulativeCostUsd / maxTurnCostUsd)) : 0;
  const pct = `${Math.round(fraction * 100)}%`;

  return (
    <Box
      role="progressbar"
      aria-label="turn budget"
      aria-valuenow={Math.round(fraction * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      sx={{
        height: 4,
        borderRadius: 999,
        bgcolor: (t) => (t.palette.mode === "light" ? "grey.200" : "grey.800"),
        overflow: "hidden",
        my: 0.5,
      }}
    >
      <Box
        data-testid="budget-meter-bar"
        className={budget.warn ? "budget-meter-bar budget-meter-bar--warn" : "budget-meter-bar"}
        sx={{
          height: "100%",
          borderRadius: 999,
          width: pct,
          bgcolor: budget.warn ? "warning.main" : "primary.main",
          // Only animate background-color, not width: this bar's width is set
          // directly from budget state (re-rendered on each iteration_end),
          // not something we want a continuous CSS transition thrashing on.
          transition: "background-color 200ms ease",
        }}
      />
    </Box>
  );
}
